'use client'

import Link from 'next/link'
import { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useProductByIdQuery } from '@/generated/graphql'
import { Button } from '@/General/Button'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useAddItemToCart } from '@/useAddItemToCart'
import { toastEmitter } from '@/General/Toast'
import {
  getCustomAddToCartFlowMetadata,
  type CustomAddToCartFlowProps,
} from '@/lib/config/customAddToCartFlows'
import { useStorefrontMarket } from '@/useStorefrontMarket'

/** Legacy AddMisc.aspx — product used for custom replacement lines. */
const MISC_PRODUCT_ID = 8640

const FALLBACK_MISC_OPTIONS: { id: number; description: string }[] = [
  { id: 3193, description: 'Partial replacement' },
  { id: 3194, description: 'Full product replacement' },
]

function productPageHrefFromApiUrl(
  url: string | null | undefined
): string | null {
  if (!url?.trim()) return null
  return `/${url.replace(/^\/+|\/+$/g, '')}`
}

function BackToProductLink({
  productUrl,
  variant,
}: {
  productUrl: string | null | undefined
  variant: 'header' | 'muted'
}) {
  const href = productPageHrefFromApiUrl(productUrl)
  if (!href) return null
  const className =
    variant === 'header'
      ? 'inline-block text-sm font-semibold text-white/90 hover:text-white mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-vi-gray rounded-sm'
      : 'inline-block text-sm font-semibold text-gray-700 hover:text-gray-900 mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2 rounded-sm'
  return (
    <Link href={href} className={className}>
      ← Back to product
    </Link>
  )
}

function getProductIdFromSearchParams(
  searchParams: Record<string, string | string[] | undefined>
): number | null {
  const raw = searchParams?.productId
  if (raw == null) return null
  const single = Array.isArray(raw) ? raw[0] : raw
  const n = parseInt(single, 10)
  return Number.isFinite(n) ? n : null
}

const MAX_MISC_PRICE = 1_000_000

/** Parse staff-entered money; supports "12.99" and "12,99" (single comma as decimal). */
function parseMoneyInput(raw: string): number {
  const t = raw.trim()
  if (t === '') return 0
  const hasDot = t.includes('.')
  const hasComma = t.includes(',')
  let normalized = t
  if (hasComma && !hasDot) {
    normalized = t.replace(',', '.')
  } else if (hasComma && hasDot) {
    normalized = t.replace(/,/g, '')
  }
  return Number.parseFloat(normalized)
}

type MiscFieldKey = 'description' | 'itemType' | 'price'

export function AddMiscAddToCart({
  flowSlug,
  searchParams,
}: CustomAddToCartFlowProps) {
  const router = useRouter()
  const flowMeta = getCustomAddToCartFlowMetadata(flowSlug)
  const storefrontMarket = useStorefrontMarket()
  const brandCode = storefrontMarket.brandCode ?? 'VANISHING_INC_US'
  const productId = getProductIdFromSearchParams(searchParams)

  const allowedIds = flowMeta?.productIds ?? [MISC_PRODUCT_ID]
  const isAllowedProduct =
    productId != null &&
    allowedIds.includes(productId) &&
    productId === MISC_PRODUCT_ID

  const [description, setDescription] = useState('')
  const [priceInput, setPriceInput] = useState('')
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [invalidFields, setInvalidFields] = useState<
    Partial<Record<MiscFieldKey, boolean>>
  >({})

  const clearValidation = useCallback(() => {
    setValidationErrors([])
    setInvalidFields({})
  }, [])

  const productQuery = useProductByIdQuery(
    { id: MISC_PRODUCT_ID, brandCode },
    { enabled: isAllowedProduct === true }
  )

  const product = productQuery.data?.ecommProductById

  const miscOptions = useMemo(() => {
    const opts = product?.options ?? []
    const mapped = opts
      .filter((o): o is typeof o & { id: number } => o.id != null)
      .map(o => ({
        id: o.id,
        description:
          (o.description ?? `Option ${o.id}`).trim() || `Option ${o.id}`,
      }))
    if (mapped.length > 0) return mapped
    return FALLBACK_MISC_OPTIONS
  }, [product?.options])

  const effectiveOptionId = selectedOptionId ?? miscOptions[0]?.id ?? null

  const firstOptionCurrency = product?.options?.[0]?.currency
  const currencySymbol = firstOptionCurrency?.symbol ?? '$'
  const currencyCode = firstOptionCurrency?.code ?? null
  const showUkVatPriceHint =
    currencyCode?.toUpperCase() === 'GBP' || currencySymbol.trim() === '£'

  const { addItem, isPending } = useAddItemToCart({
    onError: err => {
      toastEmitter.emit('toast', {
        title: err instanceof Error ? err.message : 'Could not add to cart',
        variant: 'error',
      })
    },
  })

  const handleSubmit = useCallback(async () => {
    const errors: string[] = []
    const fields: Partial<Record<MiscFieldKey, boolean>> = {}

    const trimmed = description.trim()
    if (!trimmed) {
      errors.push('Please describe this item.')
      fields.description = true
    }

    if (effectiveOptionId == null) {
      errors.push('Select an item type.')
      fields.itemType = true
    }

    const rawPrice = priceInput.trim() === '' ? '0' : priceInput.trim()
    const unitPrice = parseMoneyInput(rawPrice)
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      errors.push('Enter a valid price (zero or more).')
      fields.price = true
    } else if (unitPrice > MAX_MISC_PRICE) {
      errors.push('Price is too large.')
      fields.price = true
    }

    if (errors.length > 0) {
      setValidationErrors(errors)
      setInvalidFields(fields)
      return
    }

    setValidationErrors([])
    setInvalidFields({})

    try {
      const priceTag = showUkVatPriceHint
        ? `[${currencySymbol}${unitPrice.toFixed(2)} gross]`
        : `[${currencySymbol}${unitPrice.toFixed(2)}]`
      const descriptionForCart = `${priceTag} ${trimmed}`
      await addItem({
        productId: MISC_PRODUCT_ID,
        productOptionId: effectiveOptionId,
        description: descriptionForCart,
        quantity: 1,
        unitPrice,
      })
      router.push('/checkout')
    } catch {
      // surfaced via onError
    }
  }, [
    addItem,
    currencySymbol,
    description,
    effectiveOptionId,
    priceInput,
    router,
    showUkVatPriceHint,
  ])

  if (productId == null) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center px-4">
        <p className="text-gray-600 text-center">
          Missing product. Open this page from the misc. product with{' '}
          <code className="text-sm">?productId=…</code>.
        </p>
      </div>
    )
  }

  if (!isAllowedProduct) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center px-4 bg-gray-100">
        <p className="text-red-700 text-center max-w-md">
          This flow is only available for the misc. item product.
        </p>
      </div>
    )
  }

  if (productQuery.isLoading || !product) {
    return (
      <div className="w-full min-h-[50vh] flex flex-col">
        <div className="h-40 md:h-48 bg-vi-gray animate-pulse px-4 py-10 md:py-14">
          <div className="max-w-(--breakpoint-xl) mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {flowMeta?.title ?? 'Add a misc. item'}
            </h1>
          </div>
        </div>
        <div className="flex-1 flex justify-center items-center py-16">
          <div className="rounded-lg bg-gray-200 h-12 w-64 animate-pulse" />
        </div>
      </div>
    )
  }

  const flowTitle = flowMeta?.title ?? 'Add a misc. item'
  const flowDescription =
    flowMeta?.description ??
    'Enter a description, item type, and price for this line item.'
  const headerSubtitle = flowDescription
    ? `${flowTitle} — ${flowDescription}`
    : flowTitle

  return (
    <div className="flex w-full min-h-[min(100dvh,1200px)] flex-col bg-gray-100">
      <header className="bg-vi-gray text-white py-10 md:py-14 shrink-0 shadow-md w-full">
        <div className="max-w-(--breakpoint-xl) mx-auto px-4 xl:px-0">
          <BackToProductLink productUrl={product.url} variant="header" />
          <p className="text-gray-300 text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-2">
            Vanishing Inc.
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {product.name ?? flowTitle}
          </h1>
          <p className="mt-3 text-base md:text-lg text-white/88 max-w-3xl leading-relaxed">
            {headerSubtitle}
          </p>
        </div>
      </header>

      <main className="flex-1 w-full max-w-(--breakpoint-xl) mx-auto px-4 xl:px-0 py-8 md:py-12">
        <section className="bg-white p-6 md:p-10 rounded-xl shadow-sm border border-gray-200 max-w-xl">
          <div className="space-y-6">
            <ErrorBox errors={validationErrors} />

            <div>
              <label
                htmlFor="misc-description"
                className="block text-sm font-semibold text-gray-900 mb-2"
              >
                Describe this item
              </label>
              <textarea
                id="misc-description"
                value={description}
                onChange={e => {
                  clearValidation()
                  setDescription(e.target.value)
                }}
                rows={3}
                className={`w-full rounded-md border px-3 py-2 text-gray-900 shadow-sm focus:ring-1 ${
                  invalidFields.description
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-300'
                    : 'border-gray-300 focus:border-vi-red focus:ring-vi-red'
                }`}
                placeholder="e.g. Replacement deck — customer service"
                aria-invalid={invalidFields.description ? true : undefined}
              />
            </div>

            <div>
              <label
                htmlFor="misc-type"
                className="block text-sm font-semibold text-gray-900 mb-2"
              >
                Item type
              </label>
              <select
                id="misc-type"
                className={`w-full rounded-md border px-3 py-2 text-gray-900 shadow-sm focus:ring-1 ${
                  invalidFields.itemType
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-300'
                    : 'border-gray-300 focus:border-vi-red focus:ring-vi-red'
                }`}
                value={effectiveOptionId ?? ''}
                onChange={e => {
                  clearValidation()
                  setSelectedOptionId(Number.parseInt(e.target.value, 10))
                }}
                aria-invalid={invalidFields.itemType ? true : undefined}
              >
                {miscOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {opt.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="misc-price"
                className="block text-sm font-semibold text-gray-900 mb-2"
              >
                Price ({currencySymbol})
              </label>
              <div className="flex rounded-md shadow-sm">
                <span
                  className={`inline-flex items-center rounded-l-md border border-r-0 bg-gray-50 px-3 text-gray-600 text-sm ${
                    invalidFields.price ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  {currencySymbol}
                </span>
                <input
                  id="misc-price"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={priceInput}
                  onChange={e => {
                    clearValidation()
                    setPriceInput(e.target.value)
                  }}
                  className={`flex-1 min-w-0 rounded-r-md border px-3 py-2 text-gray-900 focus:ring-1 ${
                    invalidFields.price
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-300'
                      : 'border-gray-300 focus:border-vi-red focus:ring-vi-red'
                  }`}
                  placeholder="0.00"
                  aria-invalid={invalidFields.price ? true : undefined}
                />
              </div>
              {showUkVatPriceHint ? (
                <p className="mt-2 text-xs text-gray-500">
                  UK carts: enter price including VAT;
                </p>
              ) : null}
            </div>

            <div className="pt-2">
              <Button
                color="green"
                className="w-full sm:w-auto"
                size="lg"
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending ? 'Adding…' : 'Add to cart'}
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
